import styles from '../track-grid.module.css'

import React, { useMemo } from 'react'

import type { TrackOptions } from '@disco/state-management/actions/MediaActions'

import type { Track } from 'types/track'

import { Flex } from 'components/ds'

import type { ContentType } from '../../../../utils'
import { ContentTrackRow } from '../content-track-row'
import { SkeletonTrackRow } from '../skeleton-track-row'

export type { ContentType }

export type ContentTracksGridProps = {
  tracks: Track[]
  contentType: ContentType
  contentId: number
  contentName?: string
  businessName?: string
  contentBusinessId?: number
  isLoading: boolean
}

export const ContentTracksGrid: React.FC<ContentTracksGridProps> = ({
  tracks,
  contentType,
  contentId,
  contentName,
  businessName,
  contentBusinessId,
  isLoading,
}) => {
  const tracksArray = Array.isArray(tracks) ? tracks : []
  const loadingRows = isLoading ? Array.from({ length: 4 }, (_, i) => i) : []
  const trackMediaEventProperties = useMemo(() => {
    if (contentType === 'playlist') {
      return {
        playlistId: contentId,
        playlist: contentName,
        playlistBusiness: businessName,
        playlistBusinessId: contentBusinessId,
      }
    } else if (contentType === 'catalog') {
      return {
        catalogId: contentId,
        catalogName: contentName,
        catalogBusiness: businessName,
      }
    }
    return {}
  }, [contentType, contentId, contentName, businessName, contentBusinessId])

  const playbackContext: TrackOptions = useMemo(
    () => ({
      tracklist: tracksArray
        .filter(track => track && track.id)
        .map(track => track.id),
      playbackLocation: 'discover-music-home' as const,
      // Use clientLibraryId for both to enable tracklist-based navigation
      // (playlistId would trigger nextInPlaylist which requires playlists in Redux state)
      clientLibraryId: contentId,
    }),
    [tracksArray, contentId],
  )

  return (
    <Flex direction="column" width="100%" py="2">
      {isLoading ? (
        <div className={styles.trackList}>
          {loadingRows.map(index => (
            <SkeletonTrackRow key={index} index={index} />
          ))}
        </div>
      ) : (
        <div className={styles.trackList}>
          {tracksArray.map((track, index) => (
            <ContentTrackRow
              key={track.id}
              track={track}
              index={index}
              playbackContext={playbackContext}
              contentType={contentType}
              contentId={contentId}
              contentName={contentName}
              businessName={businessName}
              mixpanelProperties={trackMediaEventProperties}
            />
          ))}
        </div>
      )}
    </Flex>
  )
}

export default ContentTracksGrid
